/** One packed document shelf and its synchronous memory mirror. IndexedDB is
 * authoritative; localStorage only accelerates discovery and holds a best-effort
 * pagehide checkpoint. No size budget, archive tier, history trimming or eviction.
 * A successful save means transaction completion and exact committed bytes.
 * The mirror holds what `unpackText` reads: the packed form, or — for a queued
 * body whose packing is still running — its plain JSON, until `repackDocText`.
 */
import { flushForgotten, forgottenElsewhere, markMoved, markNow, type Mark } from './forgotten'

export interface DocBackend {
  all(): Promise<Array<{ id: string; raw: string }>>
  get(id: string): Promise<string | null>
  putIfNewer(
    id: string,
    text: string,
    version: number,
    expectedVersion?: number | null,
    sweep?: string | null,
  ): Promise<boolean>
  del(id: string, version?: number): Promise<void>
  clear(): Promise<void>
  ids?(): Promise<string[]>
  active?(): Promise<string | null>
  activate?(id: string, version: number): Promise<boolean>
  forget?(id: string): Promise<void>
  /** A deletion tombstone committed only while the document still stands at
   *  `version`; resolves whether it was. For removals nobody pressed for, where
   *  a save landing after the read has to win. */
  forgetIf?(id: string, version: number): Promise<boolean>
  sweepExcept?(sweep: string): Promise<void>
}

export const docEnvelope = (text: string, version: number, sweep?: string | null): string =>
  JSON.stringify({ v: version, text, ...(sweep ? { sweep } : {}) })

export function unwrapDoc(raw: string): { v: number; text: string } {
  const { v, text } = unwrapDocumentEnvelope(raw)
  return { v, text }
}
export function unwrapDocumentEnvelope(raw: string): {
  v: number
  text: string
  sweep: string | null
} {
  try {
    const held = JSON.parse(raw) as { v?: unknown; text?: unknown; sweep?: unknown }
    if (held && typeof held === 'object' && typeof held.text === 'string')
      return {
        v: typeof held.v === 'number' && Number.isFinite(held.v) ? held.v : 0,
        text: held.text,
        sweep: typeof held.sweep === 'string' ? held.sweep : null,
      }
  } catch {
    /* Bare packed and pre-envelope documents remain readable. */
  }
  return { v: 0, text: raw, sweep: null }
}

interface Held {
  text: string
  version: number
}
const cache = new Map<string, Held>()
let backend: DocBackend | null = null
let chosen = false
/** A failed boot read is not an empty shelf. Mutations remain blocked until an
 * explicit retry reads it successfully; the configured backend stays available
 * for that retry. Working copies can still be held in the packed mirror. */
export type ShelfState = 'open' | 'absent' | 'unreadable'
let shelfState: ShelfState = 'open'
export const shelfOpen = (): boolean => shelfState === 'open'
export const shelfStatus = (): ShelfState => shelfState
let hydrationFailure: unknown = null
let hydrated = false
export function setDocBackend(kv: DocBackend | null): void {
  backend = kv
  chosen = true
  shelfState = kv ? 'open' : 'absent'
  hydrationFailure = null
}
export function documentBackend(): DocBackend | null {
  if (!chosen) {
    backend = indexedDbBackend()
    chosen = true
    if (!backend) shelfState = 'absent'
  }
  return backend
}

/**
 * How many times the mirror has CHANGED — the identity of the library, for a
 * reader that derives something from every document rather than from one.
 *
 * A memo over the open exploration keys on its id and `rev`, and neither of
 * those moves when another tab saves a different exploration and
 * `refreshFromIndexEvent` refreshes the copy this tab holds. The claim index is
 * exactly that shape: built from every OTHER stored exploration, so the two
 * fields it was keyed on are the two that cannot tell it its input changed. It
 * is the same rule AGENTS.md states for the five derived maps — key on the
 * identity of every field a write would swap — with the shelf as the field.
 *
 * A counter and not a hash of the contents: this is read on every render of a
 * claim card, and every mutation of the mirror goes through the three helpers
 * below, so the count is exact for what it costs.
 */
let revision = 0
export const docsRevision = (): number => revision

/**
 * And it is PUSHED, not only pulled.
 *
 * A getter alone makes a cache key correct and leaves the reader stale: the
 * cross-tab path is `refreshFromIndexEvent` → `refreshDoc`, which resolves
 * asynchronously with nothing React is subscribed to, so a memo would keep its
 * old value until some unrelated edit re-rendered the component anyway — the
 * very staleness the counter was added to end. So the mirror announces itself,
 * and `useDocs` in the UI turns that into a render through
 * `useSyncExternalStore`.
 *
 * A listener must not throw into the mutation that notified it: a save is not
 * allowed to fail because something reading the shelf did.
 */
const listeners = new Set<() => void>()
export function subscribeDocs(onChange: () => void): () => void {
  listeners.add(onChange)
  return () => {
    listeners.delete(onChange)
  }
}
function announce(): void {
  revision++
  for (const fn of listeners) {
    try {
      fn()
    } catch {
      /* a reader's failure is not the writer's */
    }
  }
}

/* The only three ways the mirror changes. Everything below goes through them so
   that `revision` cannot be left behind by a write somebody added later. */
function hold(id: string, held: Held): void {
  cache.set(id, held)
  announce()
}
function drop(id: string): void {
  if (cache.delete(id)) announce()
}
function wipe(): void {
  const had = cache.size > 0
  cache.clear()
  if (had) announce()
}

/** The stored text of one document, or null. Synchronous: it is the mirror. */
export const readDocText = (id: string): string | null => cache.get(id)?.text ?? null
export const docVersion = (id: string): number | null => cache.get(id)?.version ?? null
export const docIds = (): string[] => [...cache.keys()]
export function cacheDocText(id: string, text: string, version: number): void {
  hold(id, { text, version })
}
/** The packed form of a body the mirror holds plain, swapped in only while the
 * mirror still holds exactly that body. A queued save mirrors its JSON at once and
 * packs it off the main thread (`packHost.ts`); `unpackText` reads both forms, so
 * the swap changes what the mirror costs to hold, never what a reader gets. */
export function repackDocText(id: string, plain: string, packed: string): void {
  const held = cache.get(id)
  if (held && held.text === plain) hold(id, { text: packed, version: held.version })
}
export function documentsHydrated(): boolean {
  return hydrated
}
export function storageHydrationFailure(): unknown {
  return hydrationFailure
}
export function setStorageHydrationFailure(error: unknown): void {
  hydrationFailure = error
}

/** Optimistic reads are useful to synchronous consumers, but cannot authorize a
 * paid call or move navigation. The caller must await this result. */
export async function writeDocText(
  id: string,
  text: string,
  version: number,
  expectedVersion?: number | null,
  sweep?: string | null,
): Promise<boolean> {
  const previous = cache.get(id)
  const mine = { text, version }
  // An older in-flight commit must not replace a newer queued working body.
  if (!previous || previous.version <= version) hold(id, mine)
  try {
    const kv = documentBackend()
    if (!kv || !shelfOpen()) throw new Error('The document database is unavailable')
    const kept = await kv.putIfNewer(id, text, version, expectedVersion, sweep)
    const raw = await kv.get(id)
    const stored = raw === null ? null : unwrapDoc(raw)
    const exact = kept && stored?.v === version && stored.text === text
    if (!exact && cache.get(id) === mine) {
      if (stored) hold(id, { text: stored.text, version: stored.v })
      else drop(id)
    }
    return exact
  } catch (error) {
    if (cache.get(id) === mine) {
      if (previous) hold(id, previous)
      else drop(id)
    }
    throw error
  }
}

/**
 * A user deletion earns a native tombstone. A conditional unwind only removes
 * its own save and can never remove a newer document from another tab.
 *
 * ACKNOWLEDGED, the way a save is (D84, D331): it resolves once the shelf has
 * committed the deletion and rejects if it did not, and the mirror lets go of
 * the document only after the commit. It used to drop the mirror first and fire
 * the transaction without waiting, so the library said "Forgotten." about a
 * document the browser then refused to delete — one global toast arrived later,
 * naming nothing, and the exploration came back on the next boot without the
 * transcript its caller had already thrown away. A refusal now leaves the
 * library exactly as it was, and the caller decides what to say.
 */
export async function deleteDocText(id: string, version?: number): Promise<void> {
  const kv = documentBackend()
  if (!kv || !shelfOpen()) throw new Error('The document database is unavailable')
  await (version === undefined && kv.forget ? kv.forget(id) : kv.del(id, version))
  drop(id)
}
export function dropDocText(id: string | null): void {
  if (id === null) wipe()
  else drop(id)
}
/** Every document, acknowledged as `deleteDocText` is: the mirror empties only
 *  once the shelf has committed the clear. */
export async function clearDocs(): Promise<void> {
  const kv = documentBackend()
  if (!kv || !shelfOpen()) throw new Error('The document database is unavailable')
  await kv.clear()
  wipe()
}

/** Boot may show the app after failure, but carries the failure to the save
 * manager. A failed read never becomes proof of an empty or deleted library. */
export async function hydrateDocs(): Promise<void> {
  try {
    const kv = documentBackend()
    if (!kv) throw new Error('The document database is unavailable')
    const rows = await kv.all()
    wipe()
    for (const { id, raw } of rows) {
      const { v, text } = unwrapDoc(raw)
      hold(id, { text, version: v })
    }
    hydrationFailure = null
    hydrated = true
    shelfState = 'open'
  } catch (error) {
    hydrationFailure = error
    hydrated = false
    shelfState = backend ? 'unreadable' : 'absent'
    throw error
  }
}
export async function refreshDoc(id: string): Promise<void> {
  const kv = documentBackend()
  if (!kv) throw new Error('The document database is unavailable')
  const raw = await kv.get(id)
  if (raw === null) {
    drop(id)
    return
  }
  const { v, text } = unwrapDoc(raw)
  const held = cache.get(id)
  if (!held || v >= held.version) hold(id, { text, version: v })
}
export function resetDocCache(): void {
  wipe()
  hydrationFailure = null
  hydrated = false
}
export async function storageEstimate(): Promise<{ usage: number; quota: number } | null> {
  try {
    const nav = typeof navigator === 'undefined' ? null : navigator
    if (!nav?.storage?.estimate) return null
    const e = await nav.storage.estimate()
    return typeof e.usage === 'number' && typeof e.quota === 'number'
      ? { usage: e.usage, quota: e.quota }
      : null
  } catch {
    return null
  }
}

const DB_NAME = 'nv-archive'
const STORE = 'docs'
const ACTIVE = ['pathways', 'active-document']
const EPOCH = ['pathways', 'document-epoch']
const deletedKey = (id: string) => ['pathways', 'deleted-document', id]
let nativeEpoch: string | null | undefined
const localSweep = (): string | null => {
  try {
    return localStorage.getItem('nv.archrm')
  } catch {
    return null
  }
}
let dbOpen: Promise<IDBDatabase | null> | null = null
function requestOpen(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    let req: IDBOpenDBRequest
    try {
      req = indexedDB.open(DB_NAME, 1)
    } catch {
      resolve(null)
      return
    }
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE)
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => resolve(null)
    req.onblocked = () => resolve(null)
  })
}
let attemptOpen = requestOpen
export function setArchiveOpener(open: (() => Promise<IDBDatabase | null>) | null): void {
  attemptOpen = open ?? requestOpen
  dbOpen = null
  nativeEpoch = undefined
}
export function openDb(): Promise<IDBDatabase | null> {
  if (dbOpen) return dbOpen
  const attempt = attemptOpen()
    .catch(() => null)
    .then((db) => {
      if (!db && dbOpen === attempt) dbOpen = null
      return db
    })
  dbOpen = attempt
  return attempt
}
async function transact<T>(
  mode: IDBTransactionMode,
  work: (s: IDBObjectStore, decide: (value: T) => void) => void,
): Promise<T> {
  const db = await openDb()
  if (!db) throw new Error('The document database could not be opened')
  return new Promise<T>((resolve, reject) => {
    let result: T
    let trx: IDBTransaction
    try {
      trx = db.transaction(STORE, mode)
      work(trx.objectStore(STORE), (value) => {
        result = value
      })
    } catch {
      reject(new Error('The document transaction could not be opened'))
      return
    }
    trx.oncomplete = () => resolve(result)
    trx.onabort = () => reject(new Error('The document write was not committed'))
    trx.onerror = () => reject(new Error('The document request failed'))
  })
}
function replayGeneration(): Mark | null {
  return flushForgotten() ? markNow('s_demo') : null
}
function indexedDbBackend(): DocBackend | null {
  if (typeof indexedDB === 'undefined') return null
  const forget = async (id: string) => {
    const replay = flushForgotten() ? markNow(id) : null
    await transact<void>('readwrite', (s) => {
      s.put({ deleted: true, replayGeneration: replay }, deletedKey(id))
      s.delete(id)
      s.get(ACTIVE).onsuccess = (e) => {
        if ((e.target as IDBRequest).result?.id === id) s.delete(ACTIVE)
      }
    })
  }
  // The same tombstone as `forget`, decided inside the transaction that writes
  // it. No replay generation: only `s_demo` is ever re-admitted over one, and
  // nothing takes that id down through here.
  const forgetIf = (id: string, version: number) =>
    transact<boolean>('readwrite', (s, decide) => {
      decide(false)
      s.get(id).onsuccess = (e) => {
        const raw = (e.target as IDBRequest).result
        if (typeof raw !== 'string' || unwrapDoc(raw).v !== version || id === 's_demo') return
        s.put({ deleted: true, replayGeneration: null }, deletedKey(id))
        s.delete(id)
        s.get(ACTIVE).onsuccess = (event) => {
          if ((event.target as IDBRequest).result?.id === id) s.delete(ACTIVE)
        }
        decide(true)
      }
    })
  const clear = async (preserveSweep?: string) => {
    const epoch = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}:${Math.random()}`
    const before = nativeEpoch
    nativeEpoch = epoch
    const replay = replayGeneration()
    try {
      await transact<void>('readwrite', (s) => {
        s.put(epoch, EPOCH)
        const req = s.openCursor()
        req.onsuccess = () => {
          const cur = req.result
          if (!cur) return
          if (
            typeof cur.key === 'string' &&
            !(
              preserveSweep &&
              typeof cur.value === 'string' &&
              unwrapDocumentEnvelope(cur.value).sweep === preserveSweep
            )
          ) {
            s.put(
              { deleted: true, replayGeneration: cur.key === 's_demo' ? replay : null },
              deletedKey(cur.key),
            )
            cur.delete()
          } else if (
            Array.isArray(cur.key) &&
            JSON.stringify(cur.key) === JSON.stringify(ACTIVE) &&
            (!preserveSweep || cur.value?.sweep !== preserveSweep)
          )
            cur.delete()
          cur.continue()
        }
      })
    } catch (error) {
      // The epoch moved in memory before the transaction that would have
      // written it, and every later save compares against it. A clear the
      // shelf refused left this tab's saves refused as conflicts until a
      // reload, against a shelf that had not changed. Put it back.
      if (nativeEpoch === epoch) nativeEpoch = before
      throw error
    }
  }
  return {
    all: () =>
      transact('readonly', (s, decide) => {
        const rows: Array<{ id: string; raw: string }> = []
        s.get(EPOCH).onsuccess = (e) => {
          nativeEpoch = (e.target as IDBRequest).result ?? null
        }
        const req = s.openCursor()
        req.onsuccess = () => {
          const cur = req.result
          if (!cur) {
            decide(rows)
            return
          }
          if (typeof cur.key === 'string' && typeof cur.value === 'string')
            rows.push({ id: cur.key, raw: cur.value })
          cur.continue()
        }
      }),
    ids: () =>
      transact('readonly', (s, decide) => {
        s.getAllKeys().onsuccess = (e) =>
          decide(
            (e.target as IDBRequest<IDBValidKey[]>).result.filter(
              (id): id is string => typeof id === 'string',
            ),
          )
      }),
    get: (id) =>
      transact('readonly', (s, decide) => {
        s.get(id).onsuccess = (e) => {
          const value = (e.target as IDBRequest).result
          decide(typeof value === 'string' ? value : null)
        }
      }),
    active: () =>
      transact('readonly', (s, decide) => {
        s.get(ACTIVE).onsuccess = (e) => {
          const p = (e.target as IDBRequest).result
          decide(typeof p?.id === 'string' ? p.id : null)
        }
      }),
    activate: (id, version) => {
      const generation = markNow(id)
      return transact('readwrite', (s, decide) => {
        decide(false)
        s.get(id).onsuccess = (e) => {
          const raw = (e.target as IDBRequest).result
          if (
            typeof raw !== 'string' ||
            unwrapDoc(raw).v !== version ||
            forgottenElsewhere(id) ||
            markMoved(id, generation)
          )
            return
          s.get(deletedKey(id)).onsuccess = (event) => {
            if ((event.target as IDBRequest).result?.deleted) return
            s.put({ id, version, sweep: localSweep() }, ACTIVE)
            decide(true)
          }
        }
      })
    },
    putIfNewer: (id, text, version, expectedVersion, suppliedSweep) => {
      const generation = markNow(id)
      const epoch = nativeEpoch
      const sweep = suppliedSweep === undefined ? localSweep() : suppliedSweep
      return transact('readwrite', (s, decide) => {
        decide(false)
        s.get(EPOCH).onsuccess = (e) => {
          const currentEpoch = (e.target as IDBRequest).result ?? null
          if (epoch !== undefined && epoch !== currentEpoch) return
          if (nativeEpoch === undefined) nativeEpoch = currentEpoch
          s.get(id).onsuccess = (event) => {
            const raw = (event.target as IDBRequest).result
            const held = typeof raw === 'string' ? unwrapDoc(raw) : null
            if (expectedVersion !== undefined && (held?.v ?? null) !== expectedVersion) return
            if (forgottenElsewhere(id) || markMoved(id, generation)) return
            s.get(deletedKey(id)).onsuccess = (deleted) => {
              const tombstone = (deleted.target as IDBRequest).result
              if (tombstone?.deleted) {
                const replay = tombstone.replayGeneration as Mark | null | undefined
                const fresh =
                  id === 's_demo' &&
                  expectedVersion === null &&
                  replay &&
                  (replay.all || replay.own) &&
                  replay.all === generation.all &&
                  replay.own === generation.own
                if (!fresh) return
                s.delete(deletedKey(id))
              }
              if (held && held.v >= version) {
                decide(held.v === version && held.text === text)
                return
              }
              s.put(docEnvelope(text, version, sweep), id)
              decide(true)
            }
          }
        }
      })
    },
    forget,
    forgetIf,
    del: async (id, version) => {
      if (version === undefined) return forget(id)
      await transact<void>('readwrite', (s) => {
        s.get(id).onsuccess = (e) => {
          const raw = (e.target as IDBRequest).result
          if (typeof raw === 'string' && unwrapDoc(raw).v === version) s.delete(id)
        }
      })
    },
    clear: () => clear(),
    sweepExcept: (sweep) => clear(sweep),
  }
}
